(() => {
  const SECTION_SELECTOR = '[data-hormonize-wholesale-bundle]';

  const parseIntSafe = (value, fallback = 0) => {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const formatMoney = (cents, moneyFormat) => {
    const amount = (Math.max(0, cents) / 100).toFixed(2);
    if (typeof moneyFormat === 'string' && moneyFormat.includes('{{amount}}')) {
      return moneyFormat.replace('{{amount}}', Number(amount).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }));
    }
    return `₱${Number(amount).toLocaleString('en-PH', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })}`;
  };

  const getQty = (item) => {
    const input = item.querySelector('[data-wholesale-qty-input]');
    if (!input) return 0;
    if (input.value === '') return 0;
    return Math.max(0, parseIntSafe(input.value, 0));
  };

  const setQty = (item, qty) => {
    const input = item.querySelector('[data-wholesale-qty-input]');
    if (!input) return;
    input.value = String(Math.max(0, qty));
  };

  const collectItems = (root) => {
    const selected = [];
    root.querySelectorAll('[data-wholesale-item]').forEach((item) => {
      const qty = getQty(item);
      if (qty < 1) return;
      const variantId = parseIntSafe(item.dataset.variantId, 0);
      if (!variantId) return;
      selected.push({
        id: variantId,
        quantity: qty,
        price: parseIntSafe(item.dataset.variantPrice, 0),
      });
    });
    return selected;
  };

  const totals = (root) => {
    const items = collectItems(root);
    return items.reduce(
      (acc, item) => {
        acc.qty += item.quantity;
        acc.cents += item.price * item.quantity;
        return acc;
      },
      { qty: 0, cents: 0, items }
    );
  };

  const updateButtonState = (root) => {
    const minQty = parseIntSafe(root.dataset.minQty, 0);
    const minTotal = parseIntSafe(root.dataset.minTotal, 0);
    const moneyFormat = root.dataset.moneyFormat || '';
    const stats = root.querySelector('[data-wholesale-stats]');
    const lineMin = root.querySelector('[data-wholesale-line-min]');
    const lineQty = root.querySelector('[data-wholesale-line-qty]');
    const linePrice = root.querySelector('[data-wholesale-line-price]');
    const terms = root.querySelector('[data-wholesale-terms]');
    const addAll = root.querySelector('[data-wholesale-add-all]');

    const { qty, cents } = totals(root);
    const meetsMinimum = qty >= minQty || cents >= minTotal;
    const termsOk = terms ? terms.checked : true;
    const canAdd = meetsMinimum && termsOk && qty > 0;

    if (lineMin) {
      lineMin.innerHTML = `Minimum <span class="hormonize-wholesale-bundle__stat-value">${minQty}</span> items <em>or</em> <span class="hormonize-wholesale-bundle__stat-value">${formatMoney(
        minTotal,
        moneyFormat
      )}</span> total required.`;
    }
    if (lineQty) {
      lineQty.innerHTML = `You currently have <span class="hormonize-wholesale-bundle__stat-value">${qty}</span> items.`;
    }
    if (linePrice) {
      linePrice.innerHTML = `Total Price: <span class="hormonize-wholesale-bundle__stat-value">${formatMoney(
        cents,
        moneyFormat
      )}</span>`;
    }

    if (stats) {
      stats.classList.toggle('is-warn', !meetsMinimum);
      stats.classList.toggle('is-ok', meetsMinimum);
      stats.classList.toggle('is-pulse', !meetsMinimum);
    }

    if (addAll) {
      addAll.disabled = !canAdd;
    }
  };

  const publishCartUpdate = async () => {
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { cart } }));
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    } catch (error) {
      console.error(error);
    }
  };

  const addBatchToCart = async (items) => {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items: items.map((item) => ({ id: item.id, quantity: item.quantity })),
      }),
    });

    if (!response.ok) {
      throw new Error('Unable to add wholesale items to cart');
    }

    return response.json();
  };

  const bindSection = (root) => {
    if (!root || root.dataset.bound === 'true') return;
    root.dataset.bound = 'true';

    const addAll = root.querySelector('[data-wholesale-add-all]');
    const terms = root.querySelector('[data-wholesale-terms]');
    const redirectToCheckout = root.dataset.redirectCheckout === 'true';

    root.addEventListener('click', (event) => {
      const minus = event.target.closest('[data-qty-action="decrease"]');
      const plus = event.target.closest('[data-qty-action="increase"]');
      if (!minus && !plus) return;
      if (!root.contains(minus || plus)) return;

      const stepper = (minus || plus).closest('[data-wholesale-stepper]');
      const item = stepper?.closest('[data-wholesale-item]');
      if (!stepper || !item) return;

      const step = parseIntSafe(stepper.dataset.step, 1);
      const init = parseIntSafe(stepper.dataset.init, 1);
      const min = parseIntSafe(stepper.dataset.min, 0);
      let current = getQty(item);

      if (minus) current = Math.max(min, current - step);
      if (plus) current = current === 0 ? init : current + step;

      setQty(item, current);
      updateButtonState(root);
    });

    root.addEventListener('input', (event) => {
      const input = event.target.closest('[data-wholesale-qty-input]');
      if (!input || !root.contains(input)) return;

      const stepper = input.closest('[data-wholesale-stepper]');
      const min = parseIntSafe(stepper?.dataset.min, 0);
      let value = input.value.replace(/[^\d]/g, '');
      if (value === '') {
        input.value = '';
        updateButtonState(root);
        return;
      }
      input.value = String(Math.max(min, parseIntSafe(value, 0)));
      updateButtonState(root);
    });

    root.addEventListener(
      'blur',
      (event) => {
        const input = event.target.closest('[data-wholesale-qty-input]');
        if (!input || !root.contains(input)) return;
        if (input.value === '') input.value = '0';
        updateButtonState(root);
      },
      true
    );

    if (terms) {
      terms.addEventListener('change', () => updateButtonState(root));
    }

    if (addAll) {
      addAll.addEventListener('click', async () => {
        const { items, qty, cents } = totals(root);
        const minQty = parseIntSafe(root.dataset.minQty, 0);
        const minTotal = parseIntSafe(root.dataset.minTotal, 0);
        const termsOk = terms ? terms.checked : true;
        const meets = (qty >= minQty || cents >= minTotal) && termsOk;
        if (!meets || items.length === 0) {
          updateButtonState(root);
          return;
        }

        const originalLabel = addAll.textContent;
        addAll.disabled = true;
        addAll.setAttribute('aria-busy', 'true');
        addAll.textContent = 'Adding…';

        try {
          await addBatchToCart(items);
          await publishCartUpdate();
          if (redirectToCheckout) {
            window.location.href = '/checkout';
            return;
          }
          addAll.textContent = 'Added';
          root.querySelectorAll('[data-wholesale-item]').forEach((item) => setQty(item, 0));
          window.setTimeout(() => {
            addAll.textContent = originalLabel || 'Add Selected to Cart';
            updateButtonState(root);
          }, 1200);
        } catch (error) {
          console.error(error);
          const lineMin = root.querySelector('[data-wholesale-line-min]');
          if (lineMin) lineMin.textContent = 'There was a problem adding to cart.';
          addAll.textContent = originalLabel || 'Add Selected to Cart';
          updateButtonState(root);
        } finally {
          addAll.removeAttribute('aria-busy');
        }
      });
    }

    updateButtonState(root);
  };

  const init = (scope = document) => {
    scope.querySelectorAll(SECTION_SELECTOR).forEach(bindSection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target) init(event.target);
  });
})();
